import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import type { ProjectRouteKey } from "@repo/shared";
import { type ComponentType, useEffect, useMemo, useRef, useState } from "react";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import { KeepAliveContainer } from "@/routing/keep-alive/KeepAliveContainer";
import { useProjectRoute } from "./project-route-context";

type AssistantPageRouteKey = "aiAssistant" | "aiAssistantNewChat" | "aiAssistantThread";

interface AssistantPageRouteState {
  routeKey: AssistantPageRouteKey;
  params: Record<string, string>;
}

interface AssistantKeepAliveProps {
  projectId: string;
  AssistantPage: ComponentType<any>;
}

export function isAssistantPageRouteKey(
  routeKey: ProjectRouteKey
): routeKey is AssistantPageRouteKey {
  return (
    routeKey === "aiAssistant" ||
    routeKey === "aiAssistantNewChat" ||
    routeKey === "aiAssistantThread"
  );
}

function resolveAssistantPageParams(route: AssistantPageRouteState): Record<string, string> {
  switch (route.routeKey) {
    case "aiAssistant":
      return {};
    case "aiAssistantNewChat":
      return { newChat: "true" };
    case "aiAssistantThread":
      return route.params;
  }
}

export function AssistantKeepAlive({ projectId, AssistantPage }: AssistantKeepAliveProps) {
  const typedProjectId = projectId as Id<"projects">;
  const { parsed } = useProjectRoute();
  const cleanupEmptyThread = useObservedMutation(api.claudia.cleanupEmptyThread, {
    feature: "assistant",
    operation: "cleanup-empty-thread",
    includeRequestId: true,
    failureOutcome: "best_effort_failure",
  });
  const previousAssistantThreadIdRef = useRef<string | null>(null);

  const activeAssistantPageRoute = useMemo<AssistantPageRouteState | null>(() => {
    if (!isAssistantPageRouteKey(parsed.routeKey)) {
      return null;
    }
    return {
      routeKey: parsed.routeKey,
      params: parsed.params,
    };
  }, [parsed.params, parsed.routeKey]);
  const [lastAssistantPageRoute, setLastAssistantPageRoute] =
    useState<AssistantPageRouteState | null>(activeAssistantPageRoute);

  useEffect(() => {
    if (activeAssistantPageRoute) {
      setLastAssistantPageRoute(activeAssistantPageRoute);
    }
  }, [activeAssistantPageRoute]);

  const activeAssistantThreadId =
    parsed.routeKey === "aiAssistantThread" ? (parsed.params.threadId ?? null) : null;

  useEffect(() => {
    const previousThreadId = previousAssistantThreadIdRef.current;
    if (previousThreadId && previousThreadId !== activeAssistantThreadId) {
      void cleanupEmptyThread({
        projectId: typedProjectId,
        threadId: previousThreadId,
        expectedSurfaceId: "chat-ui",
        source: "chat-ui.keep-alive-route-away",
      }).catch(() => {
        // Best-effort cleanup when leaving a thread route.
      });
    }
    previousAssistantThreadIdRef.current = activeAssistantThreadId;
  }, [activeAssistantThreadId, cleanupEmptyThread, typedProjectId]);

  const assistantRouteToRender = activeAssistantPageRoute ?? lastAssistantPageRoute;
  const shouldShowAssistantPage = activeAssistantPageRoute !== null;
  const assistantPageProps = assistantRouteToRender
    ? resolveAssistantPageParams(assistantRouteToRender)
    : null;

  if (!assistantPageProps) {
    return null;
  }

  return (
    <KeepAliveContainer
      active={shouldShowAssistantPage}
      variant="block"
      className="flex flex-1 flex-col min-h-0"
    >
      <AssistantPage
        key="aiAssistant"
        projectId={projectId}
        isActive={shouldShowAssistantPage}
        {...assistantPageProps}
      />
    </KeepAliveContainer>
  );
}
